/**
 * Builds the "Material Used & Condemned" report as an A4-landscape PDF — one
 * row per material-used or condemned line item (not per complaint), with each
 * complaint's shared columns spanning the rows its items occupy. jspdf +
 * jspdf-autotable are dynamically imported so they stay out of the main
 * bundle (same convention as dailyLogPdf.ts).
 */
import type { MaterialsReportLine } from '../components/MaterialsReportDoc';

export interface MaterialsReportPdfInput {
  org?: { name?: string; subtitle?: string };
  logoDataUrl?: string;
  from: string;
  to: string;
  rows: MaterialsReportLine[];
}

const NAME = 'U. N. Mehta Institute of Cardiology & Research Centre';
const SUB = 'Affiliated to B. J. Medical College, Ahmedabad-380016, INDIA';

const dmy = (d: string) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(d);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : d;
};

const fmtQty = (qty: number | '') => (qty !== '' && qty != null ? String(qty) : '');

/** One material/condemned line, exploded out of a complaint — mirrors MaterialsReportDoc.tsx's
 * flatten() so the print view and the PDF always show the same rows. Item name and qty are
 * separate columns so quantities can be scanned/totalled down the sheet. */
function flattenRows(rows: MaterialsReportLine[]) {
  const out: Array<(string | { content: string; rowSpan: number })[]> = [];
  for (const r of rows) {
    const lineCount = Math.max(r.materialsUsed.length, r.condemnedItems.length, 1);
    for (let i = 0; i < lineCount; i++) {
      const materialUsed = r.materialsUsed[i]?.item || '—';
      const materialQty = r.materialsUsed[i] ? fmtQty(r.materialsUsed[i].qty) : '';
      const condemnedItem = r.condemnedItems[i]?.item || '—';
      const condemnedQty = r.condemnedItems[i] ? fmtQty(r.condemnedItems[i].qty) : '';
      if (i === 0) {
        out.push([
          { content: r.complaintId, rowSpan: lineCount }, { content: dmy(r.date), rowSpan: lineCount },
          { content: [r.department, r.location].filter(Boolean).join(' · '), rowSpan: lineCount },
          { content: r.details, rowSpan: lineCount },
          materialUsed, materialQty, condemnedItem, condemnedQty,
          { content: r.resolvedBy, rowSpan: lineCount },
        ]);
      } else {
        out.push([materialUsed, materialQty, condemnedItem, condemnedQty]);
      }
    }
  }
  return out;
}

export async function buildMaterialsReportPdf(input: MaterialsReportPdfInput): Promise<Blob> {
  const { jsPDF } = await import('jspdf');
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const M = 10;
  const org = input.org ?? {};

  if (input.logoDataUrl) {
    try { doc.addImage(input.logoDataUrl, 'PNG', M, M, 22, 14); } catch { /* bad image */ }
  }
  doc.setTextColor(18, 58, 134);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(15);
  doc.text(org.name ?? NAME, pageW / 2, M + 6, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`(${org.subtitle ?? SUB})`, pageW / 2, M + 11.5, { align: 'center' });
  doc.setDrawColor(18, 58, 134); doc.setLineWidth(0.5);
  doc.line(M, M + 15, pageW - M, M + 15);

  doc.setTextColor(17, 17, 17);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  doc.text('Material Used & Condemned Report', pageW / 2, M + 22, { align: 'center' });
  doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
  doc.text(`From: ${dmy(input.from)}      To: ${dmy(input.to)}      Complaints: ${input.rows.length}`, pageW / 2, M + 27, { align: 'center' });

  autoTable(doc, {
    head: [
      [
        { content: 'Complaint No', rowSpan: 2 }, { content: 'Date', rowSpan: 2 },
        { content: 'Department / Location', rowSpan: 2 }, { content: 'Details', rowSpan: 2 },
        { content: 'Material Used', colSpan: 2 }, { content: 'Condemned Item', colSpan: 2 },
        { content: 'Resolved By', rowSpan: 2 },
      ],
      ['Item', 'Qty', 'Item', 'Qty'],
    ],
    body: flattenRows(input.rows) as never,
    startY: M + 32,
    margin: { left: M, right: M, bottom: 20 },
    tableLineColor: [0, 0, 0],
    tableLineWidth: 0.2,
    styles: { fontSize: 7.5, cellPadding: 1.4, lineColor: [0, 0, 0], lineWidth: 0.15, textColor: [17, 17, 17], valign: 'middle' },
    headStyles: { fillColor: [223, 230, 242], textColor: [17, 17, 17], fontStyle: 'bold', lineWidth: 0.2, halign: 'center' },
    columnStyles: {
      0: { cellWidth: 22 }, 1: { cellWidth: 18, halign: 'center' },
      3: { cellWidth: 38 },
      4: { cellWidth: 38 }, 5: { cellWidth: 14, halign: 'center' },
      6: { cellWidth: 38 }, 7: { cellWidth: 14, halign: 'center' },
      8: { cellWidth: 24 },
    },
  });

  return doc.output('blob');
}
